import { Extension } from "@tiptap/core";
import StarterKit from "@tiptap/starter-kit";
import { MdOutlineTextFields } from "react-icons/md";
import TextStyle from "@tiptap/extension-text-style";
import { useEditor, EditorContent } from "@tiptap/react";
import {
  FaBold,
  FaItalic,
  FaStrikethrough,
  FaListUl,
  FaListOl,
  FaUndo,
  FaRedo,
} from "react-icons/fa";

interface HtmlEditorProps {
  html?: string;
  onHtmlChange?: (html: string) => void;
}

export function HtmlEditor({ html, onHtmlChange }: HtmlEditorProps) {
  const editor = useEditor({
    extensions: [StarterKit, TextStyle, TextTransform],
    editorProps: {
      attributes: {
        class:
          "html-editor font-alternative px-3 py-4 rounded-lg bg-[#E0E0E0] placeholder:text-[#5B5B5B] focus:outline-none",
      },
    },
    content: html ? parseHTML(html) : "",
    onUpdate({ editor }) {
      const html = editor.getHTML();
      onHtmlChange?.(html);
    },
  });

  if (!editor) {
    return null;
  }

  const toggleUppercase = () => {
    const isUppercase = editor.isActive("textStyle", {
      textTransform: "uppercase",
    });
    if (isUppercase) editor.chain().focus().unsetTextTransform().run();
    else editor.chain().focus().setTextTransform("uppercase").run();
  };

  return (
    <div className="grid gap-2">
      {/* Toolbar */}
      <div className="flex flex-wrap gap-2">
        {/* Text Formatting Buttons */}
        <div className="flex gap-2">
          <button
            type="button"
            className="p-2 border rounded hover:bg-gray-100"
            onClick={() => editor.chain().focus().toggleBold().run()}
            disabled={!editor.can().chain().focus().toggleBold().run()}
          >
            <FaBold size={12} />
          </button>
          <button
            type="button"
            className="p-2 border rounded hover:bg-gray-100"
            onClick={() => editor.chain().focus().toggleItalic().run()}
            disabled={!editor.can().chain().focus().toggleItalic().run()}
          >
            <FaItalic size={12} />
          </button>
          <button
            type="button"
            className="p-2 border rounded hover:bg-gray-100"
            onClick={() => editor.chain().focus().toggleStrike().run()}
            disabled={!editor.can().chain().focus().toggleStrike().run()}
          >
            <FaStrikethrough size={12} />
          </button>

          {/* <button type="button"
            className="p-2 border rounded hover:bg-gray-100"
            onClick={() => {
              const url = prompt("Enter the URL");
              if (url) {
                editor.chain().focus().setLink({ href: url }).run();
              }
            }}
            disabled={!editor.can().chain().focus().setLink({ href: "" }).run()}
          >
            <FaLink  size={12}/>
          </button> */}
        </div>

        {/* List Buttons */}
        <div className="flex gap-2">
          <button
            type="button"
            className="p-2 border rounded hover:bg-gray-100"
            onClick={() => editor.chain().focus().toggleBulletList().run()}
          >
            <FaListUl size={12} />
          </button>
          <button
            type="button"
            className="p-2 border rounded hover:bg-gray-100"
            onClick={() => editor.chain().focus().toggleOrderedList().run()}
          >
            <FaListOl size={12} />
          </button>
        </div>

        <button
          type="button"
          className="p-2 border rounded hover:bg-gray-100"
          onClick={toggleUppercase}
          disabled={
            !editor
              .can()
              .chain()
              .focus()
              .setMark("textStyle", { textTransform: "uppercase" })
              .run()
          }
        >
          <MdOutlineTextFields size={12} />
        </button>

        {/* History Buttons */}
        <div className="flex gap-2 ml-auto">
          <button
            type="button"
            className="p-2 border rounded hover:bg-gray-100"
            onClick={() => editor.chain().focus().undo().run()}
            disabled={!editor.can().chain().focus().undo().run()}
          >
            <FaUndo size={12} />
          </button>
          <button
            type="button"
            className="p-2 border rounded hover:bg-gray-100"
            onClick={() => editor.chain().focus().redo().run()}
            disabled={!editor.can().chain().focus().redo().run()}
          >
            <FaRedo size={12} />
          </button>
        </div>
      </div>

      {/* Editor Content */}
      <EditorContent editor={editor} />
    </div>
  );
}

export function SimpleHtmlEditor({ html, onHtmlChange }: HtmlEditorProps) {
  const editor = useEditor({
    extensions: [StarterKit],
    editorProps: {
      attributes: {
        class: "html-editor font-alternative focus:outline-none",
      },
    },
    content: html ? parseHTML(html) : "",
    onUpdate({ editor }) {
      const html = editor.getHTML();
      onHtmlChange?.(html);
    },
  });

  if (!editor) {
    return null;
  }

  return (
    <div className="p-3">
      <div className="flex flex-wrap gap-2">
        <div className="flex gap-2">
          <button
            type="button"
            className="p-2 border rounded hover:bg-gray-100"
            onClick={() => editor.chain().focus().toggleBold().run()}
            disabled={!editor.can().chain().focus().toggleBold().run()}
          >
            <FaBold size={12} />
          </button>
          <button
            type="button"
            className="p-2 border rounded hover:bg-gray-100"
            onClick={() => editor.chain().focus().toggleItalic().run()}
            disabled={!editor.can().chain().focus().toggleItalic().run()}
          >
            <FaItalic size={12} />
          </button>
          <button
            type="button"
            className="p-2 border rounded hover:bg-gray-100"
            onClick={() => editor.chain().focus().toggleStrike().run()}
            disabled={!editor.can().chain().focus().toggleStrike().run()}
          >
            <FaStrikethrough size={12} />
          </button>
        </div>

        <div className="flex gap-2">
          <button
            type="button"
            className="p-2 border rounded hover:bg-gray-100"
            onClick={() => editor.chain().focus().toggleBulletList().run()}
          >
            <FaListUl size={12} />
          </button>
          <button
            type="button"
            className="p-2 border rounded hover:bg-gray-100"
            onClick={() => editor.chain().focus().toggleOrderedList().run()}
          >
            <FaListOl size={12} />
          </button>
        </div>
      </div>
      <EditorContent editor={editor} />
    </div>
  );
}

function parseHTML(html: string) {
  const txt = document.createElement("textarea");
  txt.innerHTML = html;
  return txt.value;
}

// Custom Extensions

export type TextTransformOptions = {
  /**
   * A list of node names where the text transform can be applied.
   * @default ['textStyle']
   * @example ['heading', 'paragraph']
   */
  types: string[];
};

declare module "@tiptap/core" {
  interface Commands<ReturnType> {
    textTransform: {
      setTextTransform: (textTransform: string) => ReturnType;
      unsetTextTransform: () => ReturnType;
    };
  }
}

export const TextTransform = Extension.create<TextTransformOptions>({
  name: "textTransform",

  addOptions() {
    return {
      types: ["textStyle"],
    };
  },

  addGlobalAttributes() {
    return [
      {
        types: this.options.types,
        attributes: {
          textTransform: {
            default: null,
            parseHTML: (element) => element.style.textTransform,
            renderHTML: (attributes) => {
              if (!attributes.textTransform) {
                return {};
              }

              return {
                style: `text-transform: ${attributes.textTransform}`,
              };
            },
          },
        },
      },
    ];
  },

  addCommands() {
    return {
      setTextTransform:
        (textTransform) =>
        ({ chain }) => {
          return chain().setMark("textStyle", { textTransform }).run();
        },
      unsetTextTransform:
        () =>
        ({ chain }) => {
          return chain()
            .setMark("textStyle", { textTransform: null })
            .removeEmptyTextStyle()
            .run();
        },
    };
  },
});
